import React, { useMemo, useRef, useState } from "react";
import styled, { css } from "styled-components";
import COLORS from "../../../assets/Colors";
import theme from "../../../assets/theme";
import useLoader from "../../../hooks/useLoader";
import {
  makeStyles,
  Grid,
  Paper,
  Typography,
  Modal,
  RadioGroup,
  FormControlLabel,
} from "@material-ui/core";
import {
  PrimaryOutlinedCTAButton,
  PrimaryCTAButton,
  PrimaryOutlinedCloseCTAButton,
} from "../../common/Buttons";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Formik } from "formik";
import * as XLSX from "xlsx";
import {
  CustomFileUpload,
  CheckboxContainer,
  CustomCheckbox,
} from "../../common/FormInputs";
import { CheckBoxLabel } from "../../common/FormInputs";
import { useSnackbar } from "notistack";
import CaseService from "../../../services/CaseService";
import { CustomInputField } from "../../common/FormInputs";
import {
  Label,
  StyledAutocomplete,
} from "../ManagementTools/component/SplitAndUpload";
import { Popper } from "@material-ui/core";
import CustomSelect from "../NoticeGeneration/component/CustomSelect";
import { bulkDownloadValidationSchema } from "../../../helpers/validationSchema";

const useStyles = makeStyles(() => ({
  formLabel: {
    fontFamily: theme.fonts.primaryFontSemiBold,
    color: COLORS.COLOR_DARK,
  },
}));

const CustomPopper = (props) => {
  const anchorEl = props.anchorEl;
  const width = anchorEl ? anchorEl.clientWidth : undefined;

  return (
    <Popper
      {...props}
      style={{
        ...props.style,
        width: width || undefined,
        zIndex: 1300,
      }}
      placement="bottom-start"
      modifiers={{
        flip: { enabled: true },
        preventOverflow: { enabled: true, boundariesElement: "scrollParent" },
      }}
    />
  );
};

const MAX_PER_BATCH = 1500;
const CONCURRENCY = 10;
const ALL = "__all";
const UNCATEGORIZED = "__uncategorized";

const fmt = (n) => Number(n || 0).toLocaleString("en-US");
const stamp = () =>
  new Date().toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });

const flattenResponse = (response, values) => {
  const out = [];
  Object.entries(response || {}).forEach(([folder, list]) => {
    (list || []).forEach((entry, i) => {
      const url = typeof entry === "string" ? entry : entry?.url;
      if (!url || !matchesType(url, values)) return;
      const category =
        typeof entry === "string"
          ? ""
          : String(entry?.noticeCategory ?? entry?.category ?? "").trim();
      out.push({ id: `${folder}|${i}|${url}`, folder, url, category });
    });
  });
  return out;
};

const matchesType = (url, values) => {
  const isDocx = url.endsWith(".docx");
  const isPdf = url.endsWith(".pdf");
  const isImage = /\.(jpg|jpeg|png|gif|tif|tiff)$/i.test(url);
  if (
    values.documentType.length === 0 ||
    values.documentType.includes("all") ||
    values?.docxToPDF
  ) {
    return true;
  }
  return (
    (values.documentType.includes("docx") && isDocx) ||
    (values.documentType.includes("pdf") && isPdf) ||
    (values.documentType.includes("images") && isImage)
  );
};

const STATUS_TONE = {
  Ready: { fg: "#1d4ed8", bg: "#e8effd", icon: "○" },
  Downloading: { fg: "#b45309", bg: "#fdf3e3", icon: "◔" },
  Downloaded: { fg: "#15803d", bg: "#e8f5ec", icon: "✓" },
  Failed: { fg: "#b42318", bg: "#fdecea", icon: "!" },
};

const BulkDownload = ({ bulkDownload, setBulkDownload, menuItems }) => {
  const classes = useStyles();
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();
  const [files, setFiles] = useState(null);
  const [scope, setScope] = useState(ALL);
  const [doneIds, setDoneIds] = useState(() => new Set());
  const [failed, setFailed] = useState({});
  const [active, setActive] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [baseName, setBaseName] = useState("documents");
  const busyRef = useRef(false);

  const viewing_items = [
    { label: "Claimant documents", name: "claimant", value: "claimant" },
    { label: "Respondent documents", name: "respondent", value: "respondent" },
    { label: "Neutral documents", name: "other", value: "other" },
  ];

  const input_actions = [
    { label: "Pdf", name: "pdf", value: "pdf" },
    { label: "Docx", name: "docx", value: "docx" },
    { label: "Images", name: "images", value: "images" },
    { label: "All", name: "all", value: "all" },
  ];

  async function s3Upload(file, filedName, setFieldValue, fileName) {
    if (file) {
      try {
        setLoader({ state: true, message: `Uploading file ${file.name}` });
        const response = await readExcelFile(file);
        setFieldValue(filedName, response);
        if (response?.length) {
          setFieldValue(fileName, file.name);
        }
      } catch (err) {
        enqueueSnackbar(err?.message, { variant: "error" });
      } finally {
        setLoader({ state: false });
      }
    }
  }

  const readExcelFile = async (file) => {
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });

    const headerRow = data[0];
    const possibleHeaders = [
      "CONTRACT NUMBER",
      "Contract No",
      "Contract Number",
      "contract no",
      "CONTRACT NO",
      "Contract No.",
    ];
    const contractNumberIndex = headerRow.findIndex((header) =>
      possibleHeaders.includes(String(header).trim()),
    );

    if (contractNumberIndex === -1) {
      enqueueSnackbar("Contract number column not found", {
        variant: "error",
      });
    }

    return data
      .slice(1)
      .map((row) => row[contractNumberIndex])
      .filter(Boolean);
  };

  async function onFormSubmit(values) {
    try {
      setLoader({ state: true, message: "" });
      const stringParams = `?contractNos=${values?.contractNumber.join(
        ",",
      )}&ownerRole=${values?.ownerType.join(",")}&noticeCategory=${
        values?.noticeCategory
      }&docxToPDF=${
        values?.documentType.includes("all") ||
        values?.documentType.includes("docx")
          ? values?.docxToPDF
            ? 1
            : 0
          : 0
      }&partyId=${values.partyId}`;

      const response =
        await CaseService.getBulkDownloadDocumentDet(stringParams);
      const list = flattenResponse(response, values);

      if (!list.length) {
        enqueueSnackbar("No files found", { variant: "error" });
        setBulkDownload(false);
        return;
      }

      setBaseName((values.fileName || "documents").replace(/\.[^.]+$/, ""));
      setDoneIds(new Set());
      setFailed({});
      setHistory([]);
      setResult(null);
      setScope(ALL);
      setFiles(list);
      enqueueSnackbar("Successfully fetched all the files", {
        variant: "success",
      });
    } catch (err) {
      enqueueSnackbar(err?.message, { variant: "error" });
    } finally {
      setLoader({ state: false });
    }
  }

  const scopes = useMemo(() => {
    if (!files) return [];
    const counts = {};
    let uncategorized = 0;
    files.forEach((f) => {
      if (f.category) counts[f.category] = (counts[f.category] || 0) + 1;
      else uncategorized += 1;
    });
    return [
      { id: ALL, label: "All Documents", count: files.length },
      ...Object.keys(counts)
        .sort()
        .map((c) => ({ id: c, label: c, count: counts[c] })),
      { id: UNCATEGORIZED, label: "Uncategorized", count: uncategorized },
    ];
  }, [files]);

  const scopeInfo = scopes.find((s) => s.id === scope) || scopes[0];

  const batches = useMemo(() => {
    if (!files) return [];
    const items =
      scope === ALL
        ? files
        : files.filter((f) =>
            scope === UNCATEGORIZED ? !f.category : f.category === scope,
          );
    const out = [];
    for (let i = 0; i < items.length; i += MAX_PER_BATCH) {
      const chunk = items.slice(i, i + MAX_PER_BATCH);
      out.push({
        n: out.length + 1,
        from: i + 1,
        to: i + chunk.length,
        items: chunk,
        pending: chunk.filter((f) => !doneIds.has(f.id)),
      });
    }
    return out;
  }, [files, scope, doneIds]);

  const total = scopeInfo?.count || 0;
  const remaining = batches.reduce((a, b) => a + b.pending.length, 0);
  const downloadedCount = total - remaining;
  const percent = total ? Math.round((downloadedCount / total) * 100) : 0;
  const nextBatch = batches.find((b) => b.pending.length > 0);
  const busy = !!active;

  const batchStatus = (b) => {
    if (active && active.scope === scope && active.n === b.n)
      return "Downloading";
    if (!b.pending.length) return "Downloaded";
    if (failed[`${scope}:${b.n}`]) return "Failed";
    return "Ready";
  };

  const runBatch = async (batch, again = false) => {
    if (busyRef.current) return;
    const items = again ? batch.items : batch.pending;
    if (!items.length) return;

    busyRef.current = true;
    const failKey = `${scope}:${batch.n}`;
    const scopeLabel = scopeInfo.label;
    setResult(null);
    setActive({
      scope,
      n: batch.n,
      done: 0,
      total: items.length,
      phase: "preparing",
    });

    try {
      const zip = new JSZip();
      const tracker = new Map();
      const okIds = [];
      let completed = 0;
      let missing = 0;

      const fetchOne = async (item) => {
        try {
          const res = await fetch(item.url);
          if (!res.ok) throw new Error(`Failed to fetch: ${item.url}`);
          const blob = await res.blob();
          let name = decodeURIComponent(item.url.split("/").pop());
          const key = `${item.folder}/${name}`;
          if (tracker.has(key)) {
            const count = tracker.get(key) + 1;
            tracker.set(key, count);
            const ext = name.includes(".") ? name.split(".").pop() : "";
            const base = ext ? name.slice(0, -(ext.length + 1)) : name;
            name = ext ? `${base}_${count}.${ext}` : `${base}_${count}`;
          } else {
            tracker.set(key, 1);
          }
          zip.folder(item.folder).file(name, blob);
          okIds.push(item.id);
        } catch (e) {
          missing += 1;
        } finally {
          completed += 1;
        }
      };

      for (let i = 0; i < items.length; i += CONCURRENCY) {
        await Promise.all(items.slice(i, i + CONCURRENCY).map(fetchOne));
        const completedCount = completed;
        setActive(
          (a) => a && { ...a, phase: "downloading", done: completedCount },
        );
      }

      if (!okIds.length) throw new Error("None of the files could be fetched.");

      setActive((a) => a && { ...a, phase: "zipping", done: items.length });
      const content = await zip.generateAsync({ type: "blob" });
      const safeScope = scopeLabel.replace(/[^A-Za-z0-9]+/g, "_");
      saveAs(content, `${baseName}_${safeScope}_batch${batch.n}.zip`);

      setDoneIds((prev) => {
        const next = new Set(prev);
        okIds.forEach((id) => next.add(id));
        return next;
      });
      setFailed((f) => {
        const n = { ...f };
        delete n[failKey];
        return n;
      });
      setHistory((h) => [
        {
          batch: batch.n,
          from: batch.from,
          to: batch.to,
          category: scope === ALL ? "All Categories" : scopeLabel,
          count: okIds.length,
          at: stamp(),
          status: "Downloaded",
        },
        ...h,
      ]);
      setResult({ type: "success", scope, n: batch.n, batch });
      if (missing) {
        enqueueSnackbar(
          `${fmt(missing)} file(s) could not be fetched and were skipped.`,
          { variant: "warning" },
        );
      }
    } catch (err) {
      setFailed((f) => ({ ...f, [failKey]: true }));
      setHistory((h) => [
        {
          batch: batch.n,
          from: batch.from,
          to: batch.to,
          category: scope === ALL ? "All Categories" : scopeLabel,
          count: items.length,
          at: stamp(),
          status: "Failed",
        },
        ...h,
      ]);
      setResult({ type: "fail", scope, n: batch.n, batch });
      enqueueSnackbar(err?.message || "Download failed", { variant: "error" });
    } finally {
      busyRef.current = false;
      setActive(null);
    }
  };

  const backToFilters = () => {
    setFiles(null);
    setResult(null);
  };

  const renderDashboard = (values) => {
    const cta = !nextBatch ? (
      <PrimaryCTAButton disabled style={{ padding: "10px 18px" }}>
        ✓ All documents downloaded
      </PrimaryCTAButton>
    ) : (
      <PrimaryCTAButton
        disabled={busy}
        style={{ padding: "10px 18px" }}
        onClick={() => runBatch(nextBatch)}
      >
        {failed[`${scope}:${nextBatch.n}`]
          ? `Retry Download (${fmt(nextBatch.pending.length)})`
          : remaining <= MAX_PER_BATCH
            ? `Download Remaining ${fmt(remaining)}`
            : `Download Next ${fmt(nextBatch.pending.length)}`}
      </PrimaryCTAButton>
    );

    return (
      <Body>
        <Card>
          <CardTitle>Download summary</CardTitle>
          <Stats>
            <Stat>
              <b>{fmt(total)}</b>
              <span>Total documents</span>
            </Stat>
            <Stat>
              <b>{fmt(downloadedCount)}</b>
              <span>Downloaded</span>
            </Stat>
            <Stat>
              <b>{fmt(remaining)}</b>
              <span>Remaining</span>
            </Stat>
            <Stat>
              <b>
                {nextBatch ? nextBatch.n : batches.length} of {batches.length}
              </b>
              <span>Current batch</span>
            </Stat>
            <Stat>
              <b>{fmt(MAX_PER_BATCH)}</b>
              <span>Max per batch</span>
            </Stat>
          </Stats>
          <Bar
            role="progressbar"
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <i style={{ width: `${percent}%` }} />
          </Bar>
          <BarLabel>
            <span>
              {fmt(downloadedCount)} / {fmt(total)} downloaded
            </span>
            <span>{percent}%</span>
          </BarLabel>
        </Card>

        <Card>
          <CardTitle>Notice category</CardTitle>
          <Chips role="group" aria-label="Notice category">
            {scopes.map((s) => (
              <ChipButton
                key={s.id}
                type="button"
                aria-pressed={s.id === scope}
                $active={s.id === scope}
                disabled={busy}
                onClick={() => {
                  setScope(s.id);
                  setResult(null);
                }}
              >
                {s.label}
                <small>{fmt(s.count)}</small>
              </ChipButton>
            ))}
          </Chips>
          <CategoryRow>
            <div>
              <CategoryName>
                {scope === ALL
                  ? "All Documents"
                  : `Notice Category: ${scopeInfo.label}`}
              </CategoryName>
              <Facts>
                <div>
                  <dt>Documents</dt>
                  <dd>{fmt(total)}</dd>
                </div>
                <div>
                  <dt>Downloaded</dt>
                  <dd>{fmt(downloadedCount)}</dd>
                </div>
                <div>
                  <dt>Remaining</dt>
                  <dd>{fmt(remaining)}</dd>
                </div>
                <div>
                  <dt>Next batch size</dt>
                  <dd>{nextBatch ? fmt(nextBatch.pending.length) : "–"}</dd>
                </div>
              </Facts>
              {scope === UNCATEGORIZED && (
                <Hint>Includes every document without a notice category.</Hint>
              )}
            </div>
            {cta}
          </CategoryRow>
        </Card>

        {active && (
          <Card $accent role="status" aria-live="polite">
            <CardTitle>
              Current download · Batch {active.n}
              {active.scope !== scope ? ` · ${active.scope}` : ""}
            </CardTitle>
            <div>
              {active.phase === "preparing"
                ? "Preparing documents…"
                : active.phase === "zipping"
                  ? "Generating ZIP file…"
                  : "Downloading…"}
            </div>
            <BigNumber>
              {fmt(active.done)} / {fmt(active.total)} documents
            </BigNumber>
            <Bar>
              <i
                style={{
                  width: `${Math.round((active.done / active.total) * 100)}%`,
                }}
              />
            </Bar>
            <BarLabel>
              <span>{Math.round((active.done / active.total) * 100)}%</span>
              <span>
                Keep this window open. Other downloads are paused until this one
                finishes.
              </span>
            </BarLabel>
          </Card>
        )}

        {!active && result?.type === "success" && (
          <Banner $tone="ok" role="status">
            <div>
              <b>✓ Batch {result.n} downloaded successfully.</b>
            </div>
            <PrimaryOutlinedCTAButton
              style={{ padding: "8px 16px" }}
              onClick={() => runBatch(result.batch, true)}
            >
              Download Again
            </PrimaryOutlinedCTAButton>
          </Banner>
        )}
        {!active && result?.type === "fail" && (
          <Banner $tone="bad" role="alert">
            <div>
              <b>! Batch {result.n} failed to download.</b>
              <Hint>Nothing was lost. You can retry this batch.</Hint>
            </div>
            <PrimaryCTAButton
              style={{ padding: "8px 16px" }}
              onClick={() => runBatch(result.batch)}
            >
              Retry Download
            </PrimaryCTAButton>
          </Banner>
        )}

        <Card>
          <CardTitle>
            Batches · {scope === ALL ? "All Documents" : scopeInfo.label}
          </CardTitle>
          <BatchGrid>
            {batches.map((b) => {
              const st = batchStatus(b);
              const isNext = nextBatch && nextBatch.n === b.n && !active;
              return (
                <BatchCard key={b.n} $next={isNext}>
                  <BatchHead>
                    <span>Batch {b.n}</span>
                    <StatusPill $tone={st}>
                      {STATUS_TONE[st].icon} {st}
                    </StatusPill>
                  </BatchHead>
                  <Hint style={{ margin: 0 }}>
                    Documents {fmt(b.from)}–{fmt(b.to)}
                  </Hint>
                  {isNext && <NextTag>Next up</NextTag>}
                  {st === "Downloaded" ? (
                    <PrimaryOutlinedCTAButton disabled style={{ padding: 8 }}>
                      Downloaded
                    </PrimaryOutlinedCTAButton>
                  ) : st === "Downloading" ? (
                    <PrimaryCTAButton disabled style={{ padding: 8 }}>
                      Downloading…
                    </PrimaryCTAButton>
                  ) : st === "Failed" ? (
                    <PrimaryCTAButton
                      disabled={busy}
                      style={{ padding: 8 }}
                      onClick={() => runBatch(b)}
                    >
                      Retry Download
                    </PrimaryCTAButton>
                  ) : isNext ? (
                    <PrimaryCTAButton
                      disabled={busy}
                      style={{ padding: 8 }}
                      onClick={() => runBatch(b)}
                    >
                      Download {fmt(b.pending.length)}
                    </PrimaryCTAButton>
                  ) : (
                    <PrimaryOutlinedCTAButton
                      disabled={busy}
                      style={{ padding: 8 }}
                      onClick={() => runBatch(b)}
                    >
                      Download {fmt(b.pending.length)}
                    </PrimaryOutlinedCTAButton>
                  )}
                </BatchCard>
              );
            })}
            {!batches.length && <Hint>No documents in this category.</Hint>}
          </BatchGrid>
        </Card>

        <Card>
          <details>
            <summary style={{ fontFamily: theme.fonts.primaryFontSemiBold }}>
              Download history ({history.length})
            </summary>
            {history.length ? (
              <TableWrap>
                <table>
                  <thead>
                    <tr>
                      <th>Batch</th>
                      <th>Documents</th>
                      <th>Notice category</th>
                      <th>Count</th>
                      <th>Date / time</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={i}>
                        <td>Batch {h.batch}</td>
                        <td>
                          {fmt(h.from)}–{fmt(h.to)}
                        </td>
                        <td>{h.category}</td>
                        <td>{fmt(h.count)} documents</td>
                        <td>{h.at}</td>
                        <td>
                          <StatusPill $tone={h.status}>
                            {STATUS_TONE[h.status].icon} {h.status}
                          </StatusPill>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableWrap>
            ) : (
              <Hint>No downloads yet.</Hint>
            )}
          </details>
        </Card>

        <Flex style={{ marginTop: 8 }}>
          <HalfWidth>
            <PrimaryOutlinedCTAButton
              style={{ padding: "10px 10px" }}
              disabled={busy}
              onClick={backToFilters}
            >
              Change filters
            </PrimaryOutlinedCTAButton>
          </HalfWidth>
          <HalfWidth>
            <PrimaryOutlinedCloseCTAButton
              style={{ padding: "10px 10px" }}
              disabled={busy}
              onClick={() => setBulkDownload(false)}
            >
              Close
            </PrimaryOutlinedCloseCTAButton>
          </HalfWidth>
        </Flex>
      </Body>
    );
  };

  return (
    <Modal
      open={bulkDownload}
      onClose={() => !busy && setBulkDownload(false)}
      disableRestoreFocus={true}
    >
      <DrawerContainer $wide={!!files}>
        <Container>
          <Paper elevation={3} style={{ padding: "16px" }}>
            <HeadingContainer>
              <Heading>Bulk Download</Heading>
            </HeadingContainer>
            <Formik
              initialValues={{
                contractNumber: "",
                fileName: "",
                ownerType: ["other"],
                documentType: ["pdf"],
                docxToPDF: false,
                noticeCategory: "",
                partyId: "",
              }}
              enableReinitialize
              validateOnBlur
              validateOnChange
              validationSchema={bulkDownloadValidationSchema}
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                errors,
                handleSubmit,
                touched,
                setFieldValue,
              }) => (
                <>
                  {files ? (
                    renderDashboard(values)
                  ) : (
                    <>
                      <Grid
                        item
                        xs={12}
                        spacing={2}
                        style={{ marginTop: "16px", padding: 16 }}
                      >
                        <RadioGroup
                          style={{
                            flexDirection: "row",
                            marginLeft: "10px",
                          }}
                          name={"ownerType"}
                        >
                          <CheckboxGridContainer>
                            {viewing_items.map((radio, key) => (
                              <FormControlLabel
                                {...{ key }}
                                classes={{ label: classes.formLabel }}
                                value={radio.value}
                                control={
                                  <CustomCheckbox
                                    size="medium"
                                    style={{ color: "#00838c" }}
                                    value={radio.value}
                                    checked={values.ownerType.includes(
                                      radio.value,
                                    )}
                                    onChange={(event) => {
                                      const { value, checked } = event.target;
                                      const newOwnerTypes = [
                                        ...values.ownerType,
                                      ];

                                      if (checked) {
                                        newOwnerTypes.push(value);
                                      } else {
                                        const index =
                                          newOwnerTypes.indexOf(value);
                                        if (index > -1) {
                                          newOwnerTypes.splice(index, 1);
                                        }
                                      }

                                      setFieldValue("ownerType", newOwnerTypes);
                                    }}
                                  />
                                }
                                label={radio.label}
                              />
                            ))}
                          </CheckboxGridContainer>
                        </RadioGroup>
                      </Grid>
                      <FormContainer>
                        <CustomSelect
                          name="partyId"
                          trigger={true}
                          getNoticeTemplates={() => {}}
                          errors={errors}
                          touched={touched}
                          setFieldValue={setFieldValue}
                          menuItems={menuItems}
                        />
                      </FormContainer>
                      <FormContainer style={{ marginTop: 5 }}>
                        <Label>Notice Category (optional)</Label>
                        <StyledAutocomplete
                          id="notice-category-autocomplete"
                          size="small"
                          options={menuItems}
                          groupBy={(option) => option.category}
                          getOptionLabel={(option) => option.name}
                          PopperComponent={CustomPopper}
                          renderInput={(params) => (
                            <div ref={params.InputProps.ref}>
                              <CustomInputField
                                {...params}
                                variant="outlined"
                                placeholder="Select notice category..."
                                style={{
                                  backgroundColor: "#ffffff",
                                  borderRadius: "10px",
                                }}
                              />
                            </div>
                          )}
                          renderGroup={(params) => (
                            <div style={{ padding: "8px 0" }}>
                              <div
                                style={{
                                  padding: "10px 16px",
                                  fontWeight: "700",
                                  fontSize: "11px",
                                  color: "#0f766e",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.8px",
                                  backgroundColor: "#f0fdf4",
                                  borderRadius: "6px",
                                  margin: "4px 8px",
                                }}
                              >
                                {params.group}
                              </div>
                              <div
                                style={{
                                  padding: "4px 0",
                                  color: COLORS.COLOR_DARK,
                                  fontFamily: theme.fonts.primaryFontSemiBold,
                                }}
                              >
                                {params.children}
                              </div>
                            </div>
                          )}
                          noOptionsText={
                            <div
                              style={{
                                color: COLORS.INPUT_LABEL,
                                fontFamily: "openSans-SemiBold, sans-serif",
                                fontSize: "14px",
                                padding: "8px",
                              }}
                            >
                              No document type found
                            </div>
                          }
                          onChange={(event, newValue) =>
                            setFieldValue(
                              "noticeCategory",
                              newValue?.name || "",
                            )
                          }
                        />
                      </FormContainer>

                      <FormContainer>
                        <Label>Upload xlsx file</Label>
                        <div style={{ marginBottom: 13 }}>
                          <CustomFileUpload
                            setFile={(file) =>
                              s3Upload(
                                file,
                                `contractNumber`,
                                setFieldValue,
                                `fileName`,
                              )
                            }
                            style={{ marginTop: 10, width: "100%" }}
                            accept=".xlsx"
                            file={{ name: values.fileName }}
                          />
                        </div>
                        <Grid
                          item
                          xs={4}
                          spacing={2}
                          style={{ marginTop: "16px", padding: 16 }}
                        >
                          <CheckboxGrid>
                            {input_actions.map((item) => (
                              <CheckboxContainer key={item.value}>
                                <CustomCheckbox
                                  name="documentType"
                                  checked={values.documentType.includes(
                                    item?.value,
                                  )}
                                  onChange={() => {
                                    if (
                                      values.documentType.includes(item.value)
                                    ) {
                                      setFieldValue(
                                        "documentType",
                                        values.documentType.filter(
                                          (arr) => arr !== item.value,
                                        ),
                                      );
                                    } else {
                                      setFieldValue("documentType", [
                                        ...values.documentType,
                                        item.value,
                                      ]);
                                    }
                                  }}
                                />
                                <CheckBoxLabel>{item.label}</CheckBoxLabel>
                              </CheckboxContainer>
                            ))}
                          </CheckboxGrid>
                        </Grid>
                        {(values?.documentType.includes("all") ||
                          values?.documentType.includes("docx")) && (
                          <Grid
                            item
                            xs={8}
                            spacing={2}
                            style={{ marginTop: "5px", paddingLeft: 16 }}
                          >
                            <CheckboxContainer>
                              <CustomCheckbox
                                name="docxToPDF"
                                checked={values.docxToPDF}
                                onChange={handleChange}
                              />
                              <CheckBoxLabel>Docx to pdf</CheckBoxLabel>
                            </CheckboxContainer>
                          </Grid>
                        )}
                        <Flex>
                          <HalfWidth>
                            <PrimaryOutlinedCloseCTAButton
                              style={{ padding: "10px 10px" }}
                              onClick={() => setBulkDownload(false)}
                            >
                              {"Cancel"}
                            </PrimaryOutlinedCloseCTAButton>
                          </HalfWidth>

                          <HalfWidth>
                            <PrimaryCTAButton
                              style={{ padding: "10px 10px" }}
                              disabled={!values.contractNumber.length}
                              onClick={handleSubmit}
                            >
                              {"Find documents"}
                            </PrimaryCTAButton>
                          </HalfWidth>
                        </Flex>
                      </FormContainer>
                    </>
                  )}
                </>
              )}
            </Formik>
          </Paper>
        </Container>
      </DrawerContainer>
    </Modal>
  );
};

export default BulkDownload;

const INK = COLORS.COLOR_DARK;
const MUTED = COLORS.INPUT_LABEL;
const LINE = COLORS.INPUT_BORDER;
const ACCENT = "#0f766e";

const Container = styled.div`
  margin: 0 15px;
  background-color: white;
  outline: none;
  border-radius: 8px;
  max-height: 92vh;
  overflow-y: auto;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
  }
`;

const FormContainer = styled.div`
  padding-top: 5px;
  padding-left: 28px;
  padding-right: 54px;
  padding-bottom: 6px;
  & form {
    padding-top: 10px;
  }
`;

const Heading = styled(Typography)(() => ({
  backgroundColor: "#A5BFCC",
  color: "#fff",
  padding: "8px",
  fontFamily: theme.fonts.primaryFontBold,
  borderRadius: "5px",
  textAlign: "center",
}));

const DrawerContainer = styled.div`
  width: 100%;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  @media ${theme?.breakpoints?.sm_up} {
    max-width: ${(p) => (p.$wide ? "920px" : "656px")};
  }
`;

const HeadingContainer = styled.div`
  padding-right: 15px;
  padding-left: 15px;
  padding-top: 10px;
`;

const Flex = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 47px;
  padding-left: 24px;
  padding-right: 24px;
`;

const HalfWidth = styled.div`
  width: 48%;
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, auto);
  grid-column-gap: 10px;
`;

const CheckboxGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, auto);
  grid-column-gap: 10px;
`;

/* Dashboard */
const Body = styled.div`
  padding: 16px 8px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;
const Card = styled.section`
  background: #fff;
  border: 1px solid ${(p) => (p.$accent ? ACCENT : LINE)};
  border-radius: 12px;
  padding: 16px;
  min-width: 0;
`;
const CardTitle = styled.h2`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${INK};
  font-size: 15px;
  margin: 0 0 12px;
`;
const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
`;
const Stat = styled.div`
  background: #f4f6f9;
  border-radius: 10px;
  padding: 10px 12px;
  b {
    display: block;
    font-family: ${theme.fonts.primaryFontSemiBold};
    font-size: 19px;
    color: ${INK};
  }
  span {
    font-family: ${theme.fonts.primaryFontRegular};
    font-size: 12px;
    color: ${MUTED};
  }
`;
const Bar = styled.div`
  height: 10px;
  background: #f0f3f6;
  border-radius: 6px;
  overflow: hidden;
  margin: 14px 0 6px;
  i {
    display: block;
    height: 100%;
    background: ${ACCENT};
    border-radius: 6px;
    transition: width 0.2s ease;
  }
`;
const BarLabel = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: ${MUTED};
`;
const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
`;
const ChipButton = styled.button`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  padding: 6px 12px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? ACCENT : LINE)};
  background: ${(p) => (p.$active ? "#e6f4f2" : "#fff")};
  color: ${(p) => (p.$active ? ACCENT : INK)};
  small {
    margin-left: 6px;
    color: ${MUTED};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${ACCENT};
    outline-offset: 2px;
  }
`;
const CategoryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
  align-items: center;
  justify-content: space-between;
`;
const CategoryName = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${INK};
  margin-bottom: 6px;
`;
const Facts = styled.dl`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 24px;
  margin: 0;
  dt {
    font-size: 12px;
    color: ${MUTED};
  }
  dd {
    margin: 0;
    font-family: ${theme.fonts.primaryFontSemiBold};
    color: ${INK};
  }
`;
const Hint = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: ${MUTED};
  margin: 6px 0 0;
`;
const BigNumber = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 22px;
  color: ${INK};
`;
const toneBox = {
  ok: { bg: "#e8f5ec", border: "#15803d" },
  bad: { bg: "#fdecea", border: "#b42318" },
};
const Banner = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px;
  padding: 14px 16px;
  font-family: ${theme.fonts.primaryFontRegular};
  color: ${INK};
  ${(p) => css`
    background: ${toneBox[p.$tone].bg};
    border: 1px solid ${toneBox[p.$tone].border};
  `}
`;
const BatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
`;
const BatchCard = styled.div`
  border: 1px solid ${(p) => (p.$next ? ACCENT : LINE)};
  box-shadow: ${(p) => (p.$next ? "0 0 0 2px #e6f4f2" : "none")};
  border-radius: 10px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const BatchHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 6px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  color: ${INK};
`;
const NextTag = styled.div`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  color: ${ACCENT};
`;
const StatusPill = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  padding: 2px 9px;
  border-radius: 20px;
  white-space: nowrap;
  color: ${(p) => STATUS_TONE[p.$tone].fg};
  background: ${(p) => STATUS_TONE[p.$tone].bg};
`;
const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: 12px;
  table {
    width: 100%;
    min-width: 620px;
    border-collapse: collapse;
    font-family: ${theme.fonts.primaryFontRegular};
    font-size: 13px;
    color: ${INK};
  }
  th,
  td {
    text-align: left;
    padding: 8px 10px;
    border-top: 1px solid ${LINE};
    white-space: nowrap;
  }
  th {
    font-size: 12px;
    color: ${MUTED};
    font-family: ${theme.fonts.primaryFontSemiBold};
  }
`;
